import { useState, useContext, useEffect } from "react"
import { LocaleContext } from "contexts"
import axios from "axios"
import cc from "classcat"
import {
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline"
import {
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid"
import Spin from "images/spin.svg"
import Modal from "components/molecules/Modal"
import useTranslations from "hooks/useTranslations"
import { siteCountry, contactFormEndpoint } from "utils/env"
import { handleOpenChat } from "utils/browser"
import countries from "../../../../config/i18n"

const locales = countries[siteCountry] || countries.fi

const themes = {
  dark: {
    bg: "bg-zinc-800",
    text: "text-zinc-50",
    button:
      "bg-kkm-darkgreen hover:bg-kkm-darkgreen/90 text-zinc-200 focus:bg-kkm-darkgreen/70",
  },
  light: {
    bg: "bg-zinc-50",
    text: "text-zinc-800",
    button:
      "bg-kkm-green hover:bg-kkm-green/90 text-zinc-800  focus:bg-kkm-green/80",
  },
  lightyellow: {
    bg: "bg-zinc-50",
    text: "text-zinc-800",
    button:
      "bg-kkm-yellow hover:bg-kkm-yellow/90 text-zinc-800  focus:bg-kkm-yellow/80",
  },
}

const Content = ({ Icon, text, description, color }) => {
  return (
    <div className="flex flex-col items-center space-y-4">
      <Icon className={cc(["w-20 ", color])} aria-hidden="true" />
      <h3 className="text-supportiveDark m-0 text-2xl font-medium leading-7">
        {text}
      </h3>
      <p className="m-0 grow whitespace-pre-line text-base text-stone-800">
        {description}
      </p>
    </div>
  )
}

const ContactItem = ({
  type,
  label,
  icon: Icon,
  content,
  href,
  onClick,
  theme = "dark",
}) => {
  const commonClasses = "flex items-center cursor-pointer text-base"

  const contentElement = (
    <>
      <Icon className="h-6 w-6 shrink-0" aria-hidden="true" />
      <span className="ml-3 whitespace-pre-line">{content}</span>
    </>
  )

  switch (type) {
    case "button":
      return (
        <div
          className={cc([
            "-ml-3 mt-3 w-fit rounded-full py-2 pl-3 pr-5 font-bold",
            themes[theme]?.button,
          ])}
          onClick={onClick}
          role="button"
          tabIndex={0}
          onKeyUp={onClick}
        >
          <dt className="sr-only">{label}</dt>
          <dd className={commonClasses}>{contentElement}</dd>
        </div>
      )
    case "link":
      return (
        <div className="mt-3">
          <dt className="sr-only">{label}</dt>
          <a href={href}>
            <dd className={commonClasses}>{contentElement}</dd>
          </a>
        </div>
      )
    case "text":
    default:
      return (
        <div className="mt-3">
          <dt className="sr-only">{label}</dt>
          <dd className={commonClasses}>{contentElement}</dd>
        </div>
      )
  }
}

const Form = ({ theme = "dark" }) => {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [submitState, setSubmitState] = useState("ready")

  const [showModal, setShowModal] = useState(false)
  const [modalContent, setModalContent] = useState({
    Icon: XCircleIcon,
    text: "",
    color: "",
  })

  const {
    contactFormName,
    contactFormEmail,
    contactFormPhone,
    contactFormMessage,
    contactFormButtonSubmit,
    contactFormSuccess,
    contactFormError,
    contactFormErrorContact,
  } = useTranslations()

  const successfulSubmission = {
    text: contactFormSuccess,
    Icon: CheckCircleIcon,
    color: "text-kkm-green",
  }

  const errorSubmission = {
    text: contactFormError,
    description: contactFormErrorContact,
    Icon: ExclamationCircleIcon,
    color: "text-red-500",
  }

  useEffect(() => {
    if (!showModal) {
      setTimeout(() => {
        setModalContent("")
      }, 300)
    }
  }, [showModal])

  useEffect(() => {
    if (submitState === "success") {
      setModalContent(successfulSubmission)
    }
    if (submitState === "error") {
      setModalContent(errorSubmission)
    }
  }, [submitState])

  const closeModal = () => {
    setShowModal(false)
  }

  const resetFields = () => {
    setName("")
    setEmail("")
    setPhone("")
    setMessage("")
  }

  const sendForm = async (event) => {
    event.preventDefault()
    let res
    setSubmitState("loading")
    try {
      res = await axios.post(
        `${contactFormEndpoint}`,
        JSON.stringify({ name, email, phone, message }),
      )
      if (res.status === 200) {
        setSubmitState("success")
        setShowModal(true)
        resetFields()
      }
    } catch (err) {
      setSubmitState("error")
      setShowModal(true)
      console.log("Error sending contact form", err)
      setTimeout(() => {
        setSubmitState("ready")
      }, 3000)
    }
    return res
  }

  const handleNameChange = (event) => {
    setName(event.target.value)
  }

  const handleEmailChange = (event) => {
    setEmail(event.target.value)
  }

  const handlePhoneChange = (event) => {
    setPhone(event.target.value)
  }

  const handleMessageChange = (event) => {
    setMessage(event.target.value)
  }

  return (
    <div className="">
      <form onSubmit={sendForm} className="m-0 grid grid-cols-1 gap-y-6">
        <div>
          <label htmlFor="name" className="sr-only">
            {contactFormName}
          </label>
          <input
            type="text"
            name="name"
            id="name"
            autoComplete="name"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormName}
            value={name}
            onChange={handleNameChange}
          />
        </div>
        <div>
          <label htmlFor="email" className="sr-only">
            {contactFormEmail}
          </label>
          <input
            name="email"
            type="email"
            autoComplete="email"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormEmail}
            value={email}
            onChange={handleEmailChange}
            // required
          />
        </div>
        <div>
          <label htmlFor="phone" className="sr-only">
            {contactFormPhone}
          </label>
          <input
            type="tel"
            name="phone"
            autoComplete="tel"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormPhone}
            value={phone}
            onChange={handlePhoneChange}
          />
        </div>
        <div>
          <label htmlFor="message" className="sr-only">
            {contactFormMessage}
          </label>
          <textarea
            name="message"
            rows={4}
            className="block w-full rounded-md border border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormMessage}
            value={message}
            onChange={handleMessageChange}
          />
        </div>
        <div className="mt-3 max-w-xs rounded-full shadow sm:mt-0 sm:shrink-0">
          <button
            className={cc([
              "transition-color flex w-full items-center justify-center rounded-full px-5 py-3 text-base font-bold leading-6 duration-150 ease-in-out focus:outline-none",
              themes[theme]?.button,
              {
                "bg-red-500 focus:bg-red-400": submitState === "error",
                "bg-kkm-green focus:bg-kkm-green": submitState === "success",
              },
            ])}
            type="submit"
          >
            {submitState === "loading" && (
              <Spin className="absolute h-5 w-5 animate-spin text-white transition-opacity" />
            )}

            <CheckCircleIcon
              className={cc([
                "absolute h-6 w-6 text-white transition-opacity",
                {
                  "opacity-100": submitState === "success",
                  "opacity-0": submitState !== "success",
                },
              ])}
            />

            {submitState === "error" && (
              <XCircleIcon className="absolute h-6 w-6 text-white transition-opacity" />
            )}
            <span
              className={cc([
                "mx-2 inline-block opacity-0 transition-opacity",
                {
                  "opacity-100": submitState === "ready",
                },
              ])}
            >
              {contactFormButtonSubmit}
            </span>
          </button>
        </div>
      </form>
      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        showCloseButton
        closeOnClickAway
      >
        <Content {...modalContent} />
      </Modal>
    </div>
  )
}

export default function Contact({ theme = "dark", children = null }) {
  const { locale } = useContext(LocaleContext)
  const {
    contactFormHeading,
    contactFormCountry,
    contactFormOffice,
    contactFormChat,
  } = useTranslations()

  const contactItems = [
    {
      type: "button",
      label: "Chat",
      icon: ChatBubbleOvalLeftEllipsisIcon,
      content: contactFormChat,
      onClick: handleOpenChat,
    },
    {
      type: "link",
      label: "Email",
      icon: EnvelopeIcon,
      content: locales[locale].contactEmail,
      href: `mailto:${locales[locale].contactEmail}`,
    },
    {
      type: "link",
      label: "Phone number",
      icon: PhoneIcon,
      content: locales[locale].contactPhone,
      href: `tel:${locales[locale].contactPhone}`,
    },
    {
      type: "link",
      label: "Address",
      icon: BuildingOfficeIcon,
      content: `Köydenpunojankatu 2,\n00180 Helsinki, ${contactFormCountry}`,
      href: `https://www.google.com/maps/search/?api=1&query=Köydenpunojankatu+2+00180+Helsinki+${contactFormCountry}`,
    },
  ]

  return (
    <div
      className={cc(["relative  py-16 lg:py-24", themes[theme]?.bg])}
      id="contact"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2
          className={cc([
            "text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl",
            themes[theme]?.text,
          ])}
        >
          {contactFormHeading}
        </h2>
        <div className="space-y-12 md:grid md:grid-cols-3 md:gap-16 md:space-y-0">
          <div className="max-w-xl space-y-10 sm:flex sm:space-x-10 sm:space-y-0 md:flex-col md:space-x-0 md:space-y-6">
            {/* office */}
            <div className={cc(["space-y-3", themes[theme]?.text])}>
              <h3
                className={cc([
                  "mb-0 text-lg font-extrabold tracking-tight sm:text-xl",
                  themes[theme]?.text,
                ])}
              >
                {contactFormOffice}
              </h3>
              <dl>
                {contactItems.map((item) => (
                  <ContactItem key={item.label} {...item} theme={theme} />
                ))}
              </dl>
            </div>
            {/* Contact person */}
            {children}
          </div>
          <div className="col-span-2">
            <div className="h-full max-w-lg sm:max-w-xl">
              <Form theme={theme} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
